// src/components/books/BookSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Users, 
  Lock, 
  Globe, 
  Building, 
  Clock, 
  Play, 
  Edit3, 
  RefreshCcw, 
  Settings, 
  Shield,
  Timer,
  Database,
  UserCheck,
  Mail,
  Trash2,
  Plus
} from 'lucide-react';
import { firestoreService } from '../../services/firestoreService';

export const BookSettings = ({ book, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('access');
  const [settings, setSettings] = useState({
    // Access Settings
    accessLevel: book?.accessLevel || 'private',
    published: book?.published || false,
    authorizedUsers: book?.authorizedUsers || [],
    allowAnonymous: book?.allowAnonymous || false,
    
    // Response Settings
    responseSettings: {
      timeLimit: book?.responseSettings?.timeLimit || null,
      allowResumeIncomplete: book?.responseSettings?.allowResumeIncomplete !== false,
      allowEditAfterSubmission: book?.responseSettings?.allowEditAfterSubmission || false,
      autoSaveInterval: book?.responseSettings?.autoSaveInterval || 30,
      requireSubmission: book?.responseSettings?.requireSubmission !== false,
      maxAttempts: book?.responseSettings?.maxAttempts || null,
      showTimer: book?.responseSettings?.showTimer !== false,
      warningTimes: book?.responseSettings?.warningTimes || [300, 60], // 5 mins, 1 min
      enableProgressTracking: book?.responseSettings?.enableProgressTracking !== false,
      allowDownloadResponses: book?.responseSettings?.allowDownloadResponses !== false,
      notifyOnSubmission: book?.responseSettings?.notifyOnSubmission || false,
      customSubmissionMessage: book?.responseSettings?.customSubmissionMessage || ''
    }
  });
  
  const [saving, setSaving] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Update settings when book changes
  useEffect(() => {
    if (book) {
      setSettings(prev => ({
        ...prev,
        accessLevel: book.accessLevel || 'private',
        published: book.published || false,
        authorizedUsers: book.authorizedUsers || [],
        allowAnonymous: book.allowAnonymous || false,
        responseSettings: {
          ...prev.responseSettings,
          ...book.responseSettings
        }
      }));
    }
  }, [book]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedBook = {
        ...book,
        ...settings,
        updatedAt: new Date()
      };
      
      await firestoreService.updateBook(book.id, updatedBook);
      onUpdate(updatedBook);
      
      // Success notification could be handled by parent component
      console.log('Settings saved successfully!');
      
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addAuthorizedUser = () => {
    setEmailError('');
    
    if (!newUserEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!validateEmail(newUserEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (settings.authorizedUsers.includes(newUserEmail)) {
      setEmailError('This email is already authorized');
      return;
    }

    setSettings(prev => ({
      ...prev,
      authorizedUsers: [...prev.authorizedUsers, newUserEmail.trim()]
    }));
    setNewUserEmail('');
  };

  const removeAuthorizedUser = (email) => {
    setSettings(prev => ({
      ...prev,
      authorizedUsers: prev.authorizedUsers.filter(user => user !== email)
    }));
  };

  const handleResponseSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      responseSettings: {
        ...prev.responseSettings,
        [key]: value
      }
    }));
  };

  const formatTime = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const tabs = [
    { id: 'access', label: 'Access & Privacy', icon: Shield },
    { id: 'responses', label: 'Response Settings', icon: Database },
    { id: 'notifications', label: 'Notifications', icon: Mail }
  ];

  const TabContent = () => {
    switch (activeTab) {
      case 'access':
        return <AccessSettings />;
      case 'responses':
        return <ResponseSettings />;
      case 'notifications':
        return <NotificationSettings />;
      default:
        return <AccessSettings />;
    }
  };

  const AccessSettings = () => (
    <div className="space-y-6">
      {/* Access Level */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-blue-600" />
          Access Level
        </h3>
        
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="accessLevel"
              value="public"
              checked={settings.accessLevel === 'public'}
              onChange={(e) => setSettings(prev => ({ ...prev, accessLevel: e.target.value }))}
              className="text-blue-600 focus:ring-blue-500"
            />
            <Globe className="h-5 w-5 text-green-500" />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Public</div>
              <div className="text-sm text-gray-600">Anyone can view this book without authentication</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="accessLevel"
              value="private"
              checked={settings.accessLevel === 'private'}
              onChange={(e) => setSettings(prev => ({ ...prev, accessLevel: e.target.value }))}
              className="text-blue-600 focus:ring-blue-500"
            />
            <Lock className="h-5 w-5 text-blue-500" />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Private</div>
              <div className="text-sm text-gray-600">Only you and authorized users can access</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              name="accessLevel"
              value="organization"
              checked={settings.accessLevel === 'organization'}
              onChange={(e) => setSettings(prev => ({ ...prev, accessLevel: e.target.value }))}
              className="text-blue-600 focus:ring-blue-500"
            />
            <Building className="h-5 w-5 text-purple-500" />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Organization</div>
              <div className="text-sm text-gray-600">Members of your organization can access</div>
            </div>
          </label>
        </div>
      </div>

      {/* Publishing Status */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-blue-600" />
          Publishing Status
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.published}
              onChange={(e) => setSettings(prev => ({ ...prev, published: e.target.checked }))}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Published</div>
              <div className="text-sm text-gray-600">Make this book visible to users based on access level</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.allowAnonymous}
              onChange={(e) => setSettings(prev => ({ ...prev, allowAnonymous: e.target.checked }))}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Allow Anonymous Access</div>
              <div className="text-sm text-gray-600">Users can access without creating an account</div>
            </div>
          </label>
        </div>
      </div>

      {/* Authorized Users (for private books) */}
      {settings.accessLevel === 'private' && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Authorized Users
          </h3>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => {
                    setNewUserEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && addAuthorizedUser()}
                  placeholder="Enter email address..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <button
                onClick={addAuthorizedUser}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
            
            {settings.authorizedUsers.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Current Authorized Users:</h4>
                {settings.authorizedUsers.map((email, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">{email}</span>
                    </div>
                    <button
                      onClick={() => removeAuthorizedUser(email)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                No authorized users added yet
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const ResponseSettings = () => (
    <div className="space-y-6">
      {/* Time Limits */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          Time Management
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                checked={!!settings.responseSettings.timeLimit}
                onChange={(e) => handleResponseSettingChange('timeLimit', e.target.checked ? 60 : null)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">Enable Time Limit</span>
            </label>
            
            {settings.responseSettings.timeLimit && (
              <div className="ml-6 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={settings.responseSettings.timeLimit || 60}
                    onChange={(e) => handleResponseSettingChange('timeLimit', parseInt(e.target.value) || null)}
                    className="w-20 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="480"
                  />
                  <span className="text-gray-600">minutes</span>
                  <span className="text-sm text-gray-500">
                    ({formatTime(settings.responseSettings.timeLimit)})
                  </span>
                </div>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.responseSettings.showTimer}
                    onChange={(e) => handleResponseSettingChange('showTimer', e.target.checked)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">Show timer to users</span>
                </label>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                checked={!!settings.responseSettings.maxAttempts}
                onChange={(e) => handleResponseSettingChange('maxAttempts', e.target.checked ? 1 : null)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="font-medium">Limit Response Attempts</span>
            </label>
            
            {settings.responseSettings.maxAttempts && (
              <div className="ml-6">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={settings.responseSettings.maxAttempts || 1}
                    onChange={(e) => handleResponseSettingChange('maxAttempts', parseInt(e.target.value) || null)}
                    className="w-20 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="10"
                  />
                  <span className="text-gray-600">attempts allowed</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Response Behavior */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Edit3 className="h-5 w-5 text-blue-600" />
          Response Behavior
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.allowResumeIncomplete}
              onChange={(e) => handleResponseSettingChange('allowResumeIncomplete', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Allow Resume Incomplete Responses</div>
              <div className="text-sm text-gray-600">Users can continue working on incomplete responses</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.allowEditAfterSubmission}
              onChange={(e) => handleResponseSettingChange('allowEditAfterSubmission', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Allow Editing After Submission</div>
              <div className="text-sm text-gray-600">Book owners can enable editing of submitted responses</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.requireSubmission}
              onChange={(e) => handleResponseSettingChange('requireSubmission', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Require Explicit Submission</div>
              <div className="text-sm text-gray-600">Users must click submit to complete their response</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.enableProgressTracking}
              onChange={(e) => handleResponseSettingChange('enableProgressTracking', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Enable Progress Tracking</div>
              <div className="text-sm text-gray-600">Track user progress through book sections</div>
            </div>
          </label>
        </div>
      </div>

      {/* Auto-Save Settings */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RefreshCcw className="h-5 w-5 text-blue-600" />
          Auto-Save Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700 mb-2">
              Auto-save interval
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={settings.responseSettings.autoSaveInterval || 30}
                onChange={(e) => handleResponseSettingChange('autoSaveInterval', parseInt(e.target.value) || 30)}
                className="w-20 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                min="10"
                max="300"
              />
              <span className="text-gray-600">seconds</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              How often to automatically save user responses
            </p>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          Data Management
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.allowDownloadResponses}
              onChange={(e) => handleResponseSettingChange('allowDownloadResponses', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Allow Response Downloads</div>
              <div className="text-sm text-gray-600">Enable CSV/Excel export of all responses</div>
            </div>
          </label>
        </div>
      </div>

      {/* Custom Submission Message */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Custom Submission Message</h3>
        <textarea
          value={settings.responseSettings.customSubmissionMessage || ''}
          onChange={(e) => handleResponseSettingChange('customSubmissionMessage', e.target.value)}
          placeholder="Enter a custom message to show users after they submit their response..."
          className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
        <p className="text-sm text-gray-500 mt-2">
          Leave blank to show the default submission message
        </p>
      </div>
    </div>
  );

  const NotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-600" />
          Email Notifications
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={settings.responseSettings.notifyOnSubmission}
              onChange={(e) => handleResponseSettingChange('notifyOnSubmission', e.target.checked)}
              className="text-blue-600 focus:ring-blue-500"
            />
            <div>
              <div className="font-medium">Notify on Response Submission</div>
              <div className="text-sm text-gray-600">Get email notifications when users submit responses</div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  if (!book) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Settings</h2>
        <p className="text-gray-600">Configure access, responses, and notifications for your book</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mb-8">
        <TabContent />
      </div>

      {/* Save Button */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-4">
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookSettings;